/**
 * The five customer-facing product domains, and how an operation's code maps
 * to one.
 *
 * The portal groups the services a customer can see into these families so the
 * list reads as clean categories rather than a flat table of codes. A service's
 * domain is derived from its `operation_code` prefix alone — the one fact the
 * customer-scoped API already carries — so nothing here needs a provider, a
 * price or any internal field.
 *
 * The legal-person family was dormant when this file was written and the four
 * product domains reflected that. That commercial decision was reversed on
 * 2026-09-03, and a missing domain here is not merely ungrouped: the services
 * page renders `PRODUCT_DOMAINS` and nothing else, so five capabilities a
 * customer had been granted were dropped from the page that lists what they
 * may call. Adding `company` is what puts them back.
 *
 * A code that belongs to no product domain — the dormant `tax.*` capability,
 * or anything unrecognised — maps to the `other` bucket. The customer portal
 * only ever renders granted permissions and a dormant capability is never
 * granted, so `other` is not expected on screen; it exists so grouping is
 * total and so a stray code is quietly left out of the product groups rather
 * than misfiled into one.
 */

/** The stable key for a product domain, plus a non-product fallback. */
export type DomainKey = 'banking' | 'cheque' | 'identity' | 'company' | 'other'

export interface Domain {
  domainKey: DomainKey
  /** The Persian group heading shown to the customer. */
  labelFa: string
}

/**
 * The four product families, in the order the product owner sells them.
 *
 * The headings are the server's own `DOMAIN_LABELS_FA`, verbatim. The price
 * endpoint publishes those same strings to consuming products, so a
 * near-synonym invented here would have the portal and the published price
 * list call one family two different things.
 *
 * There were five. `identity.mobile_match` had a heading of its own, which
 * put one service under a family name of its own on a page of four families —
 * it is an identity check, and it reads as one. The server keeps both keys,
 * because the catalogue and the price list are keyed on them; only the
 * presentation merges.
 */
export const PRODUCT_DOMAINS: readonly Domain[] = [
  { domainKey: 'identity', labelFa: 'هویت اشخاص' },
  { domainKey: 'company', labelFa: 'شخصیت حقوقی' },
  { domainKey: 'banking', labelFa: 'بانکی' },
  { domainKey: 'cheque', labelFa: 'چک و صیاد' },
]

const OTHER: Domain = { domainKey: 'other', labelFa: 'سایر' }

const byKey = (key: Exclude<DomainKey, 'other'>): Domain =>
  PRODUCT_DOMAINS.find((d) => d.domainKey === key)!

/**
 * The product domain an operation code belongs to.
 *
 * `company.mobile_match` is deliberately not an identity service: it matches a
 * number against a company rather than a person, and it is sold as part of the
 * legal-person family. The `company.` rule comes first for that reason.
 */
export function domainForCode(operationCode: string): Domain {
  if (operationCode.startsWith('banking.')) return byKey('banking')
  if (operationCode.startsWith('cheque.')) return byKey('cheque')
  if (operationCode.startsWith('company.')) return byKey('company')
  if (operationCode.startsWith('identity.')) return byKey('identity')
  return OTHER
}

/**
 * The server's own name for a service's domain, mapped to the portal's group.
 *
 * The service catalogue endpoint states the domain rather than leaving it to be
 * inferred, and stating it is the better source: the server knows which family
 * a capability belongs to, and a code-prefix rule here is a second opinion that
 * can disagree with it. The prefix rule stays as the fallback for a domain this
 * portal has not been taught, so an unrecognised value still lands somewhere
 * sensible instead of vanishing.
 */
const SERVER_DOMAIN: Record<string, Exclude<DomainKey, 'other'>> = {
  banking: 'banking',
  cheque: 'cheque',
  company: 'company',
  identity_person: 'identity',
  identity_mobile: 'identity',
}

export function domainForService(domain: string, operationCode: string): Domain {
  const key = SERVER_DOMAIN[domain]
  return key ? byKey(key) : domainForCode(operationCode)
}
